import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  BookOpen, ArrowRight, Download, ChevronRight, Loader2, Layers,
  FileText, HelpCircle, Code2, MapPin,
} from "lucide-react";
import Navbar from "@/components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIT_ACCENT = {
  doctors:         { grad: "from-emerald-50 to-white",  chip: "bg-emerald-100 text-emerald-800" },
  dentists:        { grad: "from-cyan-50 to-white",     chip: "bg-cyan-100 text-cyan-800" },
  plumbers:        { grad: "from-sky-50 to-white",      chip: "bg-sky-100 text-sky-800" },
  electricians:    { grad: "from-yellow-50 to-white",   chip: "bg-yellow-100 text-yellow-800" },
  "driver-schools":{ grad: "from-violet-50 to-white",   chip: "bg-violet-100 text-violet-800" },
  catering:        { grad: "from-rose-50 to-white",     chip: "bg-rose-100 text-rose-800" },
  bakeries:        { grad: "from-amber-50 to-white",    chip: "bg-amber-100 text-amber-800" },
  farms:           { grad: "from-lime-50 to-white",     chip: "bg-lime-100 text-lime-800" },
};

export default function IndustryKits() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [kits, setKits]   = useState([]);
  const [kit, setKit]     = useState(null);
  const [size, setSize]   = useState("standard");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Industry Kits | WebsiteTech.pro";
    axios.get(`${API}/industry-kits`).then(r => setKits(r.data.kits || []))
                                       .catch(() => {})
                                       .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!code) { setKit(null); return; }
    axios.get(`${API}/industry-kits/${code}?size=${size}`)
      .then(r => setKit(r.data))
      .catch(() => toast.error("Kit not found"));
  }, [code, size]);

  const downloadSchema = () => {
    if (!code) return;
    window.open(`${API}/industry-kits/${code}/schema.json`, "_blank");
  };

  if (loading) return (
    <div className="min-h-screen grid place-items-center bg-slate-50">
      <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
    </div>
  );

  // ------------------- Detail view -------------------
  if (kit) {
    const accent = KIT_ACCENT[kit.code] || KIT_ACCENT.doctors;
    return (
      <div className="min-h-screen bg-slate-50" data-testid="industry-kit-detail">
        <Navbar />
        <main className="max-w-6xl mx-auto px-4 py-8">
          <button onClick={() => navigate("/industry-kits")}
                  data-testid="kit-back"
                  className="text-sm text-slate-500 hover:text-slate-900 mb-3 inline-flex items-center gap-1">
            ← All kits
          </button>
          <div className={`rounded-2xl bg-gradient-to-br ${accent.grad} ring-1 ring-slate-200 p-6 sm:p-8`}>
            <div className={`inline-block text-[11px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${accent.chip}`}>
              Industry Kit · {kit.schema_type}
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3">{kit.name}</h1>
            <p className="text-slate-700 mt-2 max-w-2xl">{kit.summary}</p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <div className="inline-flex bg-white rounded-lg ring-1 ring-slate-200 p-0.5" data-testid="kit-size-toggle">
                {["standard", "deep"].map(s => (
                  <button key={s} onClick={() => setSize(s)}
                          data-testid={`kit-size-${s}`}
                          className={`px-3 py-1 text-xs font-bold rounded-md ${
                            size === s ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>
                    {s === "standard" ? "Standard · 10 pages" : "Deep · 15+ pages"}
                  </button>
                ))}
              </div>
              <button onClick={downloadSchema}
                      data-testid="kit-download-schema"
                      className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-sm font-bold inline-flex items-center gap-1">
                <Download className="w-4 h-4" /> Download JSON-LD schema
              </button>
            </div>
          </div>

          {/* Sitemap */}
          <section className="mt-6 rounded-xl bg-white ring-1 ring-slate-200 p-5"
                   data-testid="kit-sitemap">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <Layers className="w-4 h-4 text-slate-500" /> Recommended sitemap · {kit.page_count} pages
            </h2>
            <div className="mt-3 grid md:grid-cols-2 gap-3">
              {kit.pages.map((p, i) => (
                <div key={p.slug + i}
                     className="rounded-lg ring-1 ring-slate-200 bg-slate-50 p-3"
                     data-testid={`kit-page-${p.slug.replace(/[^a-z0-9]/gi, "-")}`}>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold font-mono">/{p.slug}</div>
                  <div className="font-bold text-slate-900 mt-1 text-sm">{p.h1}</div>
                  <div className="text-xs text-slate-600 mt-1">{p.meta}</div>
                  <div className="mt-2 text-[11px] text-slate-500">
                    Sections: {p.sections.join(" · ")}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQs */}
          <section className="mt-6 rounded-xl bg-white ring-1 ring-slate-200 p-5"
                   data-testid="kit-faqs">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <HelpCircle className="w-4 h-4 text-slate-500" /> FAQ starters ({kit.faqs.length})
            </h2>
            <p className="text-sm text-slate-500 mt-1">Paste into an FAQ page + attach FAQPage schema (included in the JSON-LD download).</p>
            <ul className="mt-3 space-y-2">
              {kit.faqs.map((f, i) => (
                <li key={i} className="rounded-lg ring-1 ring-slate-200 bg-slate-50 p-3"
                    data-testid={`kit-faq-${i}`}>
                  <div className="font-bold text-slate-900 text-sm">Q. {f.q}</div>
                  <div className="text-sm text-slate-600 mt-1">A. {f.a}</div>
                </li>
              ))}
            </ul>
          </section>

          {/* Schema JSON-LD preview */}
          <section className="mt-6 rounded-xl bg-white ring-1 ring-slate-200 p-5"
                   data-testid="kit-schema-preview">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-slate-900 flex items-center gap-2">
                <Code2 className="w-4 h-4 text-slate-500" /> Schema.org JSON-LD stub
              </h2>
              <button onClick={downloadSchema}
                      className="text-xs text-orange-600 font-bold hover:underline">
                Download full file
              </button>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Replace the <b>{"{...}"}</b> tokens with your business info and drop this into your <code className="bg-slate-100 px-1 rounded text-xs">&lt;head&gt;</code> inside a <code className="bg-slate-100 px-1 rounded text-xs">&lt;script type="application/ld+json"&gt;</code> tag.
            </p>
            <pre className="mt-3 rounded-lg bg-slate-900 text-slate-50 text-[11px] font-mono p-3 overflow-x-auto">
              {JSON.stringify(kit.schema_ld, null, 2)}
            </pre>
          </section>

          <div className="mt-6 rounded-xl bg-slate-900 text-white p-5 flex flex-wrap items-center gap-4 justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold">Next step</div>
              <div className="text-xl font-bold mt-1">Turn this kit into fixes in your Task Center</div>
              <div className="text-slate-300 text-sm mt-1">Run a free audit for your industry — every missing page becomes a task.</div>
            </div>
            <Link to={`/growth-audit?industry=${kit.code}`}
                  data-testid="kit-cta-audit"
                  className="rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 text-sm font-bold inline-flex items-center gap-1">
              Run growth audit <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // ------------------- List view -------------------
  return (
    <div className="min-h-screen bg-slate-50" data-testid="industry-kits-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-[11px] uppercase tracking-widest text-orange-600 font-bold">WebsiteTech · Growth System</div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-1">Industry Kits</h1>
        <p className="text-slate-600 mt-1 max-w-2xl">
          Prebuilt sitemap, schema.org markup, page copy, and FAQs for your kind of business — so your site starts life 12 months ahead of a generic template.
        </p>

        <div className="mt-6 grid md:grid-cols-3 gap-4" data-testid="industry-kits-grid">
          {kits.map(k => {
            const accent = KIT_ACCENT[k.code] || KIT_ACCENT.doctors;
            return (
              <Link key={k.code} to={`/industry-kits/${k.code}`}
                    data-testid={`kit-card-${k.code}`}
                    className={`rounded-2xl bg-gradient-to-br ${accent.grad} ring-1 ring-slate-200 hover:ring-slate-400 hover:shadow-md transition p-5 flex flex-col`}>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-slate-700" />
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${accent.chip}`}>
                    {k.schema_type}
                  </span>
                </div>
                <h2 className="font-bold text-slate-900 text-xl mt-2">{k.name}</h2>
                <p className="text-sm text-slate-600 mt-1 flex-1">{k.summary}</p>
                <div className="mt-4 flex items-center gap-3 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <Layers className="w-3.5 h-3.5" /> {k.standard_pages} pages
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> +{k.deep_extras} deep
                  </span>
                </div>
                <div className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-slate-900">
                  Open kit <ChevronRight className="w-4 h-4" />
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-8 rounded-xl bg-white ring-1 ring-slate-200 p-5 text-sm text-slate-600" data-testid="industry-kits-more">
          <FileText className="w-4 h-4 text-slate-400 inline-block mr-1" />
          More verticals coming: travel and professional services. Want yours next? <Link className="text-orange-600 font-bold underline" to="/contact">Let us know →</Link>
        </div>
      </main>
    </div>
  );
}
